import React, { useState } from 'react';
import { BACKEND_URL } from '@/lib/backendUrl';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SimpleModal } from '@/components/SimpleModal';
import { formatCurrency } from '@/lib/utils';
import { 
  ArrowDownCircle, 
  ArrowUpCircle, 
  DollarSign,
  RefreshCw,
  CheckCircle2,
  XCircle
} from 'lucide-react';

const API_URL = BACKEND_URL;

export const SangriaSuprimentoModal = ({ 
  isOpen, 
  onClose, 
  onSuccess 
}) => {
  const [tipo, setTipo] = useState('sangria');
  const [valor, setValor] = useState('');
  const [motivo, setMotivo] = useState('');
  const [observacao, setObservacao] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!valor || parseFloat(valor) <= 0) {
      setError('Informe um valor maior que zero');
      return;
    }
    
    if (!motivo.trim()) {
      setError('Informe o motivo');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/caixa/movimento`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tipo,
          valor: parseFloat(valor),
          motivo: motivo.trim(),
          observacao: observacao.trim() || null
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        onSuccess(data);
        resetForm();
        onClose();
      } else {
        setError(data.detail || 'Erro ao registrar movimento');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setTipo('sangria');
    setValor('');
    setMotivo('');
    setObservacao('');
    setError(null);
  };

  const motivosSangria = [
    'Pagamento de fornecedor',
    'Despesa operacional',
    'Retirada do proprietário',
    'Troco para outro caixa',
    'Pagamento de funcionário',
    'Outro'
  ];

  const motivosSuprimento = [
    'Reforço de troco',
    'Transferência de outro caixa',
    'Depósito inicial',
    'Correção de valor',
    'Outro'
  ];

  return (
    <SimpleModal
      open={isOpen}
      onClose={onClose}
      title={tipo === 'sangria' ? 'Sangria de Caixa' : 'Suprimento de Caixa'}
    >
      <div className="space-y-4" data-testid="sangria-suprimento-modal">
        {/* Seletor de Tipo */}
        <div className="flex gap-2">
          <button
            onClick={() => setTipo('sangria')}
            className={`flex-1 flex items-center justify-center gap-2 p-4 rounded-lg border-2 transition-colors ${
              tipo === 'sangria' 
                ? 'border-red-500 bg-red-50 text-red-700' 
                : 'border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
            data-testid="btn-sangria"
          >
            <ArrowDownCircle className="w-6 h-6" />
            <div className="text-left">
              <p className="font-semibold">Sangria</p>
              <p className="text-xs opacity-75">Retirada de dinheiro</p>
            </div>
          </button>
          
          <button
            onClick={() => setTipo('suprimento')}
            className={`flex-1 flex items-center justify-center gap-2 p-4 rounded-lg border-2 transition-colors ${
              tipo === 'suprimento' 
                ? 'border-blue-500 bg-blue-50 text-blue-700' 
                : 'border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
            data-testid="btn-suprimento"
          >
            <ArrowUpCircle className="w-6 h-6" />
            <div className="text-left">
              <p className="font-semibold">Suprimento</p>
              <p className="text-xs opacity-75">Entrada de dinheiro</p>
            </div>
          </button>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-center gap-2">
            <XCircle className="w-4 h-4" />
            {error}
          </div>
        )}

        {/* Valor */}
        <div>
          <Label htmlFor="valor">Valor *</Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">R$</span>
            <Input
              id="valor"
              type="number"
              step="0.01"
              min="0"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              placeholder="0,00"
              className="pl-10 text-lg"
              autoFocus
              data-testid="input-valor"
            />
          </div>
        </div>

        {/* Motivo */}
        <div>
          <Label htmlFor="motivo">Motivo *</Label>
          <select
            id="motivo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            className="w-full h-10 px-3 border border-slate-300 rounded-md bg-white"
            data-testid="select-motivo"
          >
            <option value="">Selecione o motivo...</option>
            {(tipo === 'sangria' ? motivosSangria : motivosSuprimento).map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>

        {/* Observação */}
        <div>
          <Label htmlFor="observacao">Observação (opcional)</Label>
          <Input
            id="observacao"
            value={observacao}
            onChange={(e) => setObservacao(e.target.value)}
            placeholder="Detalhes adicionais..."
            data-testid="input-observacao"
          />
        </div>

        {/* Resumo */}
        {valor && parseFloat(valor) > 0 && (
          <div className={`p-4 rounded-lg ${tipo === 'sangria' ? 'bg-red-50' : 'bg-blue-50'}`}>
            <div className="flex items-center justify-between">
              <span className={tipo === 'sangria' ? 'text-red-700' : 'text-blue-700'}>
                {tipo === 'sangria' ? 'Valor a retirar:' : 'Valor a adicionar:'}
              </span>
              <span className={`text-xl font-bold ${tipo === 'sangria' ? 'text-red-700' : 'text-blue-700'}`}>
                {formatCurrency(parseFloat(valor) || 0)}
              </span>
            </div>
          </div>
        )}

        {/* Botões */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading || !valor || !motivo}
            className={tipo === 'sangria' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}
            data-testid="btn-confirmar"
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle2 className="w-4 h-4 mr-2" />
            )}
            Confirmar {tipo === 'sangria' ? 'Sangria' : 'Suprimento'}
          </Button>
        </div>
      </div>
    </SimpleModal>
  );
};
